import React from 'react';

export const StructuredData: React.FC = () => {
  const baseUrl = 'https://sudiptadhara.in';

  // Person Schema
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Sudipta Dhara',
    alternateName: 'Sudipta',
    jobTitle: ['Lead Generative AI Developer', 'Game Backend Developer', 'Software Developer'],
    datePublished: '2025-12-22',
    dateModified: '2025-12-22',
    worksFor: {
      '@type': 'Organization',
      name: 'DECA Games',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Kolkata',
        addressRegion: 'West Bengal',
        addressCountry: 'IN'
      }
    },
    knowsAbout: [
      'Generative AI',
      'RAG Pipelines',
      'Agentic AI',
      'LangChain',
      'LangGraph',
      'Python',
      'PHP',
      'Django',
      'Node.js',
      'Backend Development',
      'Vector Databases',
      'LLM Applications',
      'Game Development',
      'FastAPI',
      'OpenAI API',
      'Shell Scripting',
      'ZSH',
      'PostgreSQL',
      'Docker',
      'AWS'
    ],
    url: baseUrl,
    image: `${baseUrl}/sudipta.jpeg`,
    email: 'admin@sudiptadhara.in',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'A-T6-1602, The Soul',
      addressLocality: 'New Town',
      addressRegion: 'West Bengal',
      postalCode: '700135',
      addressCountry: 'IN'
    },
    taxID: '19AVZPD8368C1ZY'
  };

  // WebSite Schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Sudipta Dhara - Portfolio',
    url: baseUrl,
    datePublished: '2025-12-22',
    dateModified: '2025-12-22',
    description: 'Portfolio of Sudipta Dhara - Software Developer, Generative AI Engineer, and creator of innovative tech products including Talk2Term, MegaSearch, ProDev\'s News, MeetEcho, RitualRhythms, MySmartNest, and ProDevs.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/?s={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  // Organization Schema for DECA Games
  const decaGamesSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'DECA Games',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Kolkata',
      addressRegion: 'West Bengal',
      addressCountry: 'IN'
    }
  };

  // SoftwareApplication Schemas for Products
  const products = [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Talk2Term',
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Unix, Linux, macOS',
      description: 'A ZSH plugin that converts plain English into perfect shell commands using AI. Stop googling CLI flags—just type in English and get executable commands.',
      url: 'https://talk2term.prodevs.in',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: [
        'GPT-powered command generation',
        'Free daily credits (5 Lite requests)',
        'Native ZSH integration',
        'Convert English to shell commands'
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'MegaSearch',
      applicationCategory: 'WebApplication',
      operatingSystem: 'Any',
      description: 'An intelligent metasearch engine that queries multiple sources simultaneously, analyzes results with AI, and synthesizes comprehensive answers with citations.',
      url: 'https://megasearch.prodevs.in',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: [
        'Parallel execution across 10+ search sources',
        'AI-powered analysis and refinement',
        'Native MCP server support',
        'Synthesized answers with citations'
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: "ProDev's News",
      applicationCategory: 'NewsApplication',
      operatingSystem: 'Any',
      description: 'Personalized AI/ML news digest automatically aggregated and deduplicated from 50+ trusted sources. Stay ahead in AI without the noise.',
      url: 'https://news.prodevs.in',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: [
        '50+ trusted news sources',
        'Smart deduplication',
        'Personalized news feed',
        'Audio podcast summaries'
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'MeetEcho',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Any',
      description: 'Transform audio and video files into actionable insights. Transcribe, chat with your content using AI, and generate intelligent summaries.',
      url: 'https://meetecho.prodevs.in',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: [
        'Audio & video transcription (50+ languages)',
        'Chat with your content using AI',
        'Smart summaries with customizable templates',
        '99% accuracy transcription'
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'RitualRhythms',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Any',
      description: 'Comprehensive society management platform streamlining operations, enhancing resident experience, and building stronger communities.',
      url: 'https://ritualrhythms.in',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: [
        'Event management and smart food coupons',
        'Donation management and member directory',
        'Analytics & reports',
        'QR-based food distribution'
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'MySmartNest',
      applicationCategory: 'HomeApplication',
      operatingSystem: 'iOS, Android',
      description: 'Premium smart home automation solutions combining cutting-edge technology with intuitive design. Transform your home with intelligent automation.',
      url: 'https://mysmartnest.in',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: [
        'Enhanced security with motion detection',
        'Energy efficiency optimization',
        'Complete home control from anywhere',
        'Smart home automation'
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'ProDevs',
      url: 'https://prodevs.in',
      description: 'A comprehensive technology platform providing expert insights, technical blogs, video tutorials, and curated content for tech enthusiasts.',
      publisher: {
        '@type': 'Organization',
        name: 'ProDevs'
      }
    }
  ];

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'About',
        item: `${baseUrl}#about`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Products',
        item: `${baseUrl}#products`
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: 'Experience',
        item: `${baseUrl}#experience`
      },
      {
        '@type': 'ListItem',
        position: 5,
        name: 'Skills',
        item: `${baseUrl}#skills`
      },
      {
        '@type': 'ListItem',
        position: 6,
        name: 'Contact',
        item: `${baseUrl}#contact`
      }
    ]
  };

  const allSchemas = [
    personSchema,
    websiteSchema,
    decaGamesSchema,
    breadcrumbSchema,
    ...products
  ];

  return (
    <>
      {allSchemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 2) }}
        />
      ))}
    </>
  );
};

